import { useCallback, useEffect, useState } from 'react';

/**
 * Rows for a server-paginated DataTable.
 * fetchPage({ offset, limit }) resolves to { data, total }. `key` identifies everything
 * else the request depends on (entity id, filters); a new key starts again at page 1.
 * Returns { rows, total, loading, error, retry, pagination } where pagination plugs into DataTable.
 */
export default function usePagedQuery(fetchPage, key, { enabled = true, initialRowsPerPage = 50 } = {}) {
  const [paging, setPaging] = useState({ key, page: 0 });
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState({ rows: [], total: 0, loading: enabled, error: null });
  const page = paging.key === key ? paging.page : 0;

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    setResult((prev) => ({ ...prev, loading: true, error: null }));
    fetchPage({ offset: page * rowsPerPage, limit: rowsPerPage }).then(
      (res) => live && setResult({ rows: res?.data || [], total: res?.total || 0, loading: false, error: null }),
      (error) => live && setResult({ rows: [], total: 0, loading: false, error }),
    );
    return () => {
      live = false;
    };
    // fetchPage is expected to change only with key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, page, rowsPerPage, attempt, enabled]);

  const onPageChange = useCallback((next) => setPaging({ key, page: next }), [key]);
  const onRowsPerPageChange = useCallback(
    (next) => {
      setRowsPerPage(next);
      setPaging({ key, page: 0 });
    },
    [key],
  );
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    ...result,
    retry,
    pagination: { total: result.total, page, rowsPerPage, onPageChange, onRowsPerPageChange },
  };
}
